// /src/components/core/ErrorReportModal.jsx
// Modal component for users to report problems/issues with the application
// Captures user location in site, context, detailed feedback, report type, and user identity

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from './Modal';
import { redactAddress, redactDebugContext, redactText } from '../../utils/redactTokens';

const REPORT_TYPES = [
  { value: 'bug', labelKey: 'type_bug_label', descriptionKey: 'type_bug_description' },
  { value: 'suggestion', labelKey: 'type_suggestion_label', descriptionKey: 'type_suggestion_description' },
  { value: 'question', labelKey: 'type_question_label', descriptionKey: 'type_question_description' },
  { value: 'other', labelKey: 'type_other_label', descriptionKey: '' }
];

const SYMPTOM_CHIPS = {
  general: [
    { id: 'page-wont-load', labelKey: 'symptom_page_wont_load' },
    { id: 'button-broken', labelKey: 'symptom_button_broken' },
    { id: 'looks-wrong', labelKey: 'symptom_looks_wrong' },
    { id: 'too-slow', labelKey: 'symptom_too_slow' },
    { id: 'error-message', labelKey: 'symptom_error_message' }
  ],
  form: [
    { id: 'wont-save', labelKey: 'symptom_wont_save' },
    { id: 'lost-work', labelKey: 'symptom_lost_work' },
    { id: 'upload-failed', labelKey: 'symptom_upload_failed' }
  ],
  auth: [
    { id: 'cant-login', labelKey: 'symptom_cant_login' },
    { id: 'cant-register', labelKey: 'symptom_cant_register' },
    { id: 'password-reset', labelKey: 'symptom_password_reset' }
  ]
};

// Determine which symptom chips are relevant based on context
function getRelevantChips(currentView, currentContext) {
  const component = currentContext?.component || '';
  const view = (currentView || '').toLowerCase();

  let chips = [...SYMPTOM_CHIPS.general];

  if (component.startsWith('AddEdit') || view.includes('new-') || view.includes('form')) {
    chips = [...chips, ...SYMPTOM_CHIPS.form];
  }

  if (component === 'LoginRegisterModal' || component === 'AuthRequiredModal' ||
      component === 'ForgotPasswordModal' ||
      view.includes('login') || view.includes('register') || view.includes('password')) {
    chips = [...chips, ...SYMPTOM_CHIPS.auth];
  }

  return chips;
}

// Generate a contextual sentence starter based on where the user is
// Returns a translation key instead of a hardcoded string
function getContextualPromptKey(currentView, currentContext) {
  const component = currentContext?.component || '';
  const view = (currentView || '').toLowerCase();

  if (component === 'AddEditArticle' || view.includes('new-article'))
    return 'context_prompt_create_article';
  if (component === 'AddEditChat' || view.includes('new-chat'))
    return 'context_prompt_create_chat';
  if (component === 'AddEditEvent' || view.includes('new-event'))
    return 'context_prompt_create_event';
  if (component === 'AddEditGroup' || view.includes('new-group'))
    return 'context_prompt_create_group';
  if ((view.includes('article') || view === 'article detail') && !view.includes('new'))
    return 'context_prompt_reading_article';
  if ((view.includes('chat') || view === 'chat detail') && !view.includes('new'))
    return 'context_prompt_in_chat';
  if ((view.includes('event') || view === 'event detail') && !view.includes('new'))
    return 'context_prompt_viewing_event';
  if (component === 'MainFeed' && view === 'news')
    return 'context_prompt_browsing_news';
  if (component === 'MainFeed' && view === 'chat')
    return 'context_prompt_browsing_chats';
  if (component === 'MainFeed' && view === 'events')
    return 'context_prompt_browsing_events';
  if (view === 'groups' || view.includes('group'))
    return 'context_prompt_browsing_groups';
  if (component === 'LoginRegisterModal')
    return 'context_prompt_login_register';
  if (component === 'ForgotPasswordModal')
    return 'context_prompt_password_reset';
  if (component === 'AuthRequiredModal')
    return 'context_prompt_auth_required';
  if (view.includes('location') || component === 'MyLocations')
    return 'context_prompt_managing_locations';
  if (view.includes('profile') || component === 'MyProfile')
    return 'context_prompt_editing_profile';
  if (view.includes('image') || component === 'MyImages')
    return 'context_prompt_managing_images';
  return '';
}

// Get user_id from localStorage auth token (same pattern as Architecture.md)
function getUserId() {
  try {
    const storageKey = Object.keys(localStorage).find(
      k => k.startsWith('sb-') && k.endsWith('-auth-token')
    );
    if (storageKey) {
      const session = JSON.parse(localStorage.getItem(storageKey));
      return session?.user?.id || null;
    }
  } catch {
    // Silently fail for anonymous users
  }
  return null;
}

function ErrorReportModal({ isOpen, onClose, currentView, currentContext }) {
  const { t } = useTranslation('error_report');
  const [feedback, setFeedback] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [whatExpected, setWhatExpected] = useState('');
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [email, setEmail] = useState('');
  const [reportType, setReportType] = useState('bug');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const theme = useTheme();

  // Pre-fill the "what happened" field with context when modal opens or report type changes to bug
  useEffect(() => {
    if (isOpen && reportType === 'bug' && !whatHappened) {
      const promptKey = getContextualPromptKey(currentView, currentContext);
      const prompt = promptKey ? t(promptKey) : '';
      setWhatHappened(prompt);
    }
  }, [isOpen, reportType]);

  const toggleSymptom = (chipId) => {
    setSelectedSymptoms(prev =>
      prev.includes(chipId)
        ? prev.filter(id => id !== chipId)
        : [...prev, chipId]
    );
  };

  // Build rich debug context for programmers
  const buildDebugContext = () => {
    const debugInfo = {
      // Component/page info from props
      component: currentContext?.component || null,
      selectedArea: currentContext?.selectedArea || null,
      selectedLocality: currentContext?.selectedLocality || null,
      itemId: currentContext?.itemId || null,
      itemType: currentContext?.itemType || null,
      groupId: currentContext?.groupId || null,

      // URL analysis
      pathname: window.location.pathname,
      hash: window.location.hash,
      search: window.location.search,
      urlSegments: window.location.pathname.split('/').filter(Boolean),

      // Browser/environment
      screenSize: `${window.screen.width}x${window.screen.height}`,
      devicePixelRatio: window.devicePixelRatio,
      online: navigator.onLine,
      language: navigator.language,

      // Performance hints
      memoryUsage: performance.memory ? {
        usedJSHeapSize: Math.round(performance.memory.usedJSHeapSize / 1048576) + 'MB',
        totalJSHeapSize: Math.round(performance.memory.totalJSHeapSize / 1048576) + 'MB'
      } : null,

      // Recent console errors (populated by errorCapture.js)
      recentErrors: window.__ournetErrors?.slice(-5) || [],

      // Navigation + action breadcrumb trail (populated by errorCapture.js)
      breadcrumbs: window.__ournetBreadcrumbs?.slice(-20) || [],

      // Selected symptom chips (if any)
      selectedSymptoms: selectedSymptoms.length > 0 ? selectedSymptoms : null
    };

    // Remove null/undefined/empty values for cleaner output
    return Object.fromEntries(
      Object.entries(debugInfo).filter(([_, v]) => {
        if (v == null || v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate based on report type
    if (reportType === 'bug') {
      if (!whatHappened.trim()) {
        setInlineError(t('validation_what_happened'));
        return;
      }
    } else {
      if (!feedback.trim()) {
        setInlineError(t('validation_feedback'));
        return;
      }
    }
    setInlineError('');

    setIsSubmitting(true);

    // Build the user feedback text
    let userFeedback;
    if (reportType === 'bug') {
      const symptomLabels = selectedSymptoms.map(id => {
        const allChips = [...SYMPTOM_CHIPS.general, ...SYMPTOM_CHIPS.form, ...SYMPTOM_CHIPS.auth];
        return allChips.find(c => c.id === id)?.label || id;
      });
      const symptomLine = symptomLabels.length > 0 ? `Symptoms: ${symptomLabels.join(', ')}\n\n` : '';
      const expectedLine = whatExpected.trim() ? `\n\nExpected: ${whatExpected.trim()}` : '';
      userFeedback = `${symptomLine}What happened: ${whatHappened.trim()}${expectedLine}`;
    } else {
      userFeedback = feedback.trim();
    }

    // No credential is stored in a problem report (Roadmap #90): invitation and join-link tokens,
    // sign-in callback parameters and verification links are removed from the address, the debug
    // context (field by field) and the reporter's own text. Every error_reports writer must do this.
    const debugContext = redactDebugContext(buildDebugContext());
    const userId = getUserId();

    const errorReport = {
      timestamp: new Date().toISOString(),
      page_url: redactAddress(window.location.href),
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      current_view: currentView || 'Unknown',
      current_context: currentContext?.name || 'Unknown',
      context_type: currentContext?.type || 'Unknown',
      user_feedback: redactText(userFeedback),
      user_email: email.trim() || null,
      report_type: reportType,
      user_id: userId,
      debug_context: JSON.stringify(debugContext)
    };

    try {
      const { error } = await supabaseFetch.insert('error_reports', errorReport, { returnMinimal: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      setFeedback('');
      setWhatHappened('');
      setWhatExpected('');
      setSelectedSymptoms([]);
      setEmail('');
      setReportType('bug');
      setShowSuccess(true);

      window.dispatchEvent(new CustomEvent('ournet:issues-updated'));
    } catch (error) {
      console.error('Error submitting report:', error);
      if (error?.code !== 'AUTH_REQUIRED') {
        setInlineError(t('error_submit_failed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorCount = window.__ournetErrors?.length || 0;
  const relevantChips = getRelevantChips(currentView, currentContext);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      noHelp
      alignTop
      panelStyle={{
        background: theme?.cardBg || 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}
    >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 'bold',
            color: theme?.text?.primary || '#1A1A1A'
          }}>
            {t('modal_title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              color: theme?.text?.secondary || '#666'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = theme?.bg || '#F5F5F5'}
            onMouseOut={(e) => e.currentTarget.style.background = 'none'}
          >
            ×
          </button>
        </div>

        {/* Success confirmation */}
        {showSuccess && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '24px 0 8px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#22C55E20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              fontSize: '28px'
            }}>
              ✓
            </div>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '16px',
              color: theme?.text?.primary || '#1A1A1A',
              lineHeight: '1.5'
            }}>
              {t('success_message')}
            </p>
            <button
              onClick={() => { setShowSuccess(false); onClose(); }}
              style={{
                padding: '10px 32px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                background: theme?.primary || '#024B99',
                color: theme?.text?.inverse || 'white',
                cursor: 'pointer'
              }}
            >
              {t('common:ok')}
            </button>
          </div>
        )}

        {/* Info + form (hidden when success screen is showing) */}
        {!showSuccess && <>
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: theme?.text?.secondary || '#666',
          lineHeight: '1.5'
        }}>
          {t('modal_description')}
        </p>

        {/* Data that will be submitted */}
        <div style={{
          background: theme?.bg || '#F5F5F5',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '13px',
          color: theme?.text?.secondary || '#666'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: theme?.text?.primary || '#1A1A1A' }}>
            {t('data_included_label')}
          </div>

          <div><strong>{t('data_page_label')}</strong> {currentView || t('unknown_value')}</div>
          {currentContext?.component && (
            <div><strong>{t('data_component_label')}</strong> {currentContext.component}</div>
          )}
          {currentContext?.name && currentContext.name !== 'General' && (
            <div><strong>{t('data_context_label')}</strong> {currentContext.name} ({currentContext?.type || t('unknown_value')})</div>
          )}
          {currentContext?.selectedArea && (
            <div><strong>{t('data_area_label')}</strong> {currentContext.selectedArea}</div>
          )}
          {currentContext?.selectedLocality && (
            <div><strong>{t('data_locality_label')}</strong> {currentContext.selectedLocality}</div>
          )}
          {currentContext?.itemId && (
            <div><strong>{t('data_item_label')}</strong> {currentContext.itemType || 'item'} #{currentContext.itemId.substring(0, 8)}...</div>
          )}
          <div><strong>{t('data_viewport_label')}</strong> {window.innerWidth}x{window.innerHeight}</div>
          <div><strong>{t('data_url_label')}</strong> {window.location.pathname}</div>
          {errorCount > 0 && (
            <div style={{ color: '#DC2626' }}><strong>{t('data_js_errors_label')}</strong> {t('data_js_errors_count', { count: errorCount })}</div>
          )}

          {/* Toggle for technical details */}
          <button
            type="button"
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 0 0 0',
              fontSize: '12px',
              color: theme?.primary || '#024B99',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {showTechnicalDetails ? t('hide_technical_details') : t('show_technical_details')}
          </button>

          {showTechnicalDetails && (
            <div style={{
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: `1px solid ${theme?.border || '#DDD'}`,
              fontSize: '11px',
              fontFamily: 'monospace'
            }}>
              <div><strong>{t('tech_screen_label')}</strong> {window.screen.width}x{window.screen.height} @{window.devicePixelRatio}x</div>
              <div><strong>{t('tech_online_label')}</strong> {navigator.onLine ? t('tech_online_yes') : t('tech_online_no')}</div>
              <div><strong>{t('tech_language_label')}</strong> {navigator.language}</div>
              {performance.memory && (
                <div><strong>{t('tech_memory_label')}</strong> {Math.round(performance.memory.usedJSHeapSize / 1048576)}MB / {Math.round(performance.memory.totalJSHeapSize / 1048576)}MB</div>
              )}
              <div style={{
                marginTop: '4px',
                wordBreak: 'break-all',
                maxHeight: '60px',
                overflow: 'auto'
              }}>
                <strong>{t('tech_browser_label')}</strong> {navigator.userAgent}
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Report Type Selector */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: theme?.text?.primary || '#1A1A1A',
              marginBottom: '8px'
            }}>
              {t('report_type_label')}
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {REPORT_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setReportType(type.value)}
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: reportType === type.value ? 'bold' : 'normal',
                    border: reportType === type.value
                      ? `2px solid ${theme?.primary || '#024B99'}`
                      : `1px solid ${theme?.border || '#DDD'}`,
                    borderRadius: '20px',
                    background: reportType === type.value
                      ? (theme?.primary || '#024B99') + '15'
                      : 'transparent',
                    color: reportType === type.value
                      ? (theme?.primary || '#024B99')
                      : (theme?.text?.secondary || '#666'),
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {t(type.labelKey)}
                </button>
              ))}
            </div>
            {REPORT_TYPES.find(rt => rt.value === reportType)?.descriptionKey && (
              <div style={{
                fontSize: '12px',
                color: theme?.text?.secondary || '#999',
                marginTop: '4px'
              }}>
                {t(REPORT_TYPES.find(rt => rt.value === reportType).descriptionKey)}
              </div>
            )}
          </div>

          {/* Bug-specific: Symptom Chips + Two-Field Flow */}
          {reportType === 'bug' ? (
            <>
              {/* Symptom Chips */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: theme?.text?.primary || '#1A1A1A',
                  marginBottom: '8px'
                }}>
                  {t('symptoms_label')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {relevantChips.map(chip => {
                    const isSelected = selectedSymptoms.includes(chip.id);
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => toggleSymptom(chip.id)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          borderRadius: '16px',
                          border: isSelected
                            ? `2px solid ${theme?.primary || '#024B99'}`
                            : `1px solid ${theme?.border || '#DDD'}`,
                          background: isSelected
                            ? (theme?.primary || '#024B99') + '20'
                            : 'transparent',
                          color: isSelected
                            ? (theme?.primary || '#024B99')
                            : (theme?.text?.secondary || '#666'),
                          cursor: 'pointer',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {t(chip.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* What happened? */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: theme?.text?.primary || '#1A1A1A',
                  marginBottom: '4px'
                }}>
                  {t('what_happened_label')}
                </div>
                <textarea
                  value={whatHappened}
                  onChange={(e) => setWhatHappened(e.target.value)}
                  placeholder={t('what_happened_placeholder')}
                  required
                  autoFocus
                  maxLength={5000}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: `1px solid ${theme?.border || '#DDD'}`,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* What did you expect? */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: theme?.text?.primary || '#1A1A1A',
                  marginBottom: '4px'
                }}>
                  {t('what_expected_label')} <span style={{ fontWeight: 'normal', color: theme?.text?.secondary || '#999' }}>({t('optional_label')})</span>
                </div>
                <textarea
                  value={whatExpected}
                  onChange={(e) => setWhatExpected(e.target.value)}
                  placeholder={t('what_expected_placeholder')}
                  maxLength={2000}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: `1px solid ${theme?.border || '#DDD'}`,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </>
          ) : (
            /* Non-bug: Single textarea with type-specific placeholder */
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={
                reportType === 'suggestion' ? t('feedback_placeholder_suggestion') :
                reportType === 'question' ? t('feedback_placeholder_question') :
                t('feedback_placeholder_other')
              }
              required
              autoFocus
              maxLength={5000}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                fontSize: '14px',
                borderRadius: '8px',
                border: `1px solid ${theme?.border || '#DDD'}`,
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '16px',
                boxSizing: 'border-box'
              }}
            />
          )}

          {/* Email Field */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '13px',
              color: theme?.text?.secondary || '#666',
              lineHeight: '1.4'
            }}>
              {t('email_description')}
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('email_placeholder')}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                borderRadius: '8px',
                border: `1px solid ${theme?.border || '#DDD'}`,
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Inline error */}
          {inlineError && (
            <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 12px 0' }}>{inlineError}</p>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: `1px solid ${theme?.border || '#DDD'}`,
                borderRadius: '8px',
                background: 'transparent',
                color: theme?.text?.primary || '#1A1A1A',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                background: theme?.primary || '#024B99',
                color: theme?.text?.inverse || 'white',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? t('submitting_button') : t('submit_button')}
            </button>
          </div>
        </form>
        </>}
    </Modal>
  );
}

export default ErrorReportModal;
